import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { useAppState } from '../../../../AppStateProvider';
import { Tooltip } from '../../../../components/Tooltip';
import type { Reaction } from '../../../../domain/generatedEmail';

import { StyledFooterButton, StyledFooterButtonTooltip } from './styles';

export type FeedbackButtonProps = {
  reaction: Reaction;
  isLoading: boolean;
  requestId?: number;
  onFeedback(reaction: Reaction): void;
};

export const FeedbackButton = ({ reaction, isLoading, onFeedback, requestId }: FeedbackButtonProps) => {
  const { emailReactions } = useAppState().emailGenerationOptions;
  const { t } = useTranslation();

  const iconName = useMemo(() => {
    if (reaction === 'like') {
      return emailReactions[requestId!] === 'like' ? 'thumb-up' : 'thumb-up-outline';
    }

    return emailReactions[requestId!] === 'dislike' ? 'thumb-down' : 'thumb-down-outline';
  }, [emailReactions, requestId, reaction]);

  return (
    <Tooltip
      content={<StyledFooterButtonTooltip>{t(`insights.email.${reaction}`)}</StyledFooterButtonTooltip>}
      enabled={!isLoading}
    >
      <StyledFooterButton
        variant="flat"
        onClick={() => onFeedback(reaction)}
        flip={reaction === 'dislike' ? emailReactions[requestId!] === reaction : null}
        disabled={isLoading}
        data-automation="FooterButton"
      >
        <SWReactIcons
          iconName={iconName}
          size="xs"
          iconColor={isLoading ? colorsPalettes.carbon[100] : colorsPalettes.carbon[300]}
        />
      </StyledFooterButton>
    </Tooltip>
  );
};
